/**
 * AccouPro — MySQL Database & Auth Client
 * 
 * Replaces Supabase with a high-performance MySQL connection layer
 * via the local Express backend API (/api/db/query and /api/auth).
 */

const API_BASE = ""; // Uses relative URL with Vite proxy to http://localhost:5000

/**
 * Safely parse HTTP response ensuring that unexpected HTML (502, 504, 404, Cloudflare errors)
 * does not cause SyntaxError: Unexpected token '<'.
 */
export async function safeParseApiResponse<T = any>(res: Response): Promise<{
  ok: boolean;
  status: number;
  data: T | null;
  error: Error | null;
}> {
  const contentType = res.headers.get("content-type") || "";
  let text = "";

  try {
    text = await res.text();
  } catch (readErr: any) {
    return {
      ok: false,
      status: res.status,
      data: null,
      error: new Error(`Failed to read server response: ${readErr.message || "Network error"}`),
    };
  }

  const trimmed = text.trim();
  const isHtml = trimmed.startsWith("<") || contentType.includes("text/html");

  if (isHtml) {
    let msg = `Server returned HTML (${res.status})`;
    if (res.status === 502) {
      msg = "API backend service is currently unreachable (502 Bad Gateway). Please try again in a few moments.";
    } else if (res.status === 503) {
      msg = "Service is temporarily unavailable (503). Please try again shortly.";
    } else if (res.status === 504) {
      msg = "Gateway timed out waiting for backend server (504). Please try again.";
    } else if (res.status === 404) {
      msg = "Requested API endpoint not found (404).";
    } else if (res.status === 403) {
      msg = "Access forbidden by security proxy or gateway (403).";
    } else if (!res.ok) {
      msg = `Server error (${res.status}). Please try again later.`;
    }
    return {
      ok: false,
      status: res.status,
      data: null,
      error: new Error(msg),
    };
  }

  if (!trimmed) {
    if (!res.ok) {
      return {
        ok: false,
        status: res.status,
        data: null,
        error: new Error(`Server returned empty response with status ${res.status}`),
      };
    }
    return { ok: true, status: res.status, data: null, error: null };
  }

  try {
    const json = JSON.parse(trimmed);
    if (!res.ok || json.error) {
      const errorMsg = json.error?.message || json.message || `Request failed with status ${res.status}`;
      return {
        ok: false,
        status: res.status,
        data: json.data || null,
        error: new Error(errorMsg),
      };
    }
    return {
      ok: true,
      status: res.status,
      data: json.data !== undefined ? json.data : json,
      error: null,
    };
  } catch {
    return {
      ok: false,
      status: res.status,
      data: null,
      error: new Error(
        !res.ok
          ? `Server returned HTTP ${res.status}: ${trimmed.slice(0, 100)}`
          : "Invalid JSON response from server."
      ),
    };
  }
}

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  role?: string;
  company?: string | null;
  company_id?: string | null;
  user_metadata?: Record<string, unknown>;
}

export interface Session {
  access_token: string;
  user: AuthUser;
}

type AuthListener = (event: string, session: Session | null) => void;

class MySQLAuthClient {
  private authUser: AuthUser | null = null;
  private session: Session | null = null;
  private listeners: AuthListener[] = [];
  private initialized = false;

  constructor() {
    this.init();
  }

  private init() {
    if (this.initialized) return;
    this.initialized = true;

    try {
      const savedUser = localStorage.getItem("accoupro_auth_user");
      const savedSession = localStorage.getItem("accoupro_auth_session");
      if (savedUser) {
        this.authUser = JSON.parse(savedUser);
      }
      if (savedSession) {
        this.session = JSON.parse(savedSession);
      }
    } catch (e) {
      console.warn("[MySQL Auth] Error reading cached session", e);
    }
  }

  async getUser(): Promise<{ data: { user: AuthUser | null }; error: Error | null }> {
    this.init();
    if (!this.authUser) {
      return { data: { user: null }, error: null };
    }
    return { data: { user: this.authUser }, error: null };
  }

  async getSession(): Promise<{ data: { session: Session | null }; error: Error | null }> {
    this.init();
    return { data: { session: this.session }, error: null };
  }

  async signInWithPassword({ email, password }: { email: string; password: string }) {
    this.init();
    try {
      if (!email || !password || !email.trim() || !password.trim()) {
        return { data: { user: null, session: null }, error: new Error("Email and password are required") };
      }

      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      const parsed = await safeParseApiResponse<any>(res);
      if (!parsed.ok || parsed.error) {
        throw parsed.error || new Error("Invalid credentials");
      }

      this.authUser = parsed.data.user;
      this.session = parsed.data.session;

      localStorage.setItem("accoupro_auth_user", JSON.stringify(this.authUser));
      localStorage.setItem("accoupro_auth_session", JSON.stringify(this.session));

      this.trigger("SIGNED_IN", this.session);
      return { data: parsed.data, error: null };
    } catch (err: any) {
      return { data: { user: null, session: null }, error: err };
    }
  }

  async signUp({ email, password, options }: { email: string; password: string; options?: { data?: Record<string, unknown> } }) {
    this.init();
    try {
      if (!email || !password || !email.trim() || !password.trim()) {
        return { data: { user: null, session: null }, error: new Error("Email and password are required") };
      }

      const res = await fetch(`${API_BASE}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, options }),
      });

      const parsed = await safeParseApiResponse<any>(res);
      if (!parsed.ok || parsed.error) {
        throw parsed.error || new Error("Registration failed");
      }

      this.authUser = parsed.data.user;
      this.session = parsed.data.session;

      localStorage.setItem("accoupro_auth_user", JSON.stringify(this.authUser));
      localStorage.setItem("accoupro_auth_session", JSON.stringify(this.session));

      this.trigger("SIGNED_IN", this.session);
      return { data: parsed.data, error: null };
    } catch (err: any) {
      return { data: { user: null, session: null }, error: err };
    }
  }

  async updateUser(attributes: { password?: string; current_password?: string; currentPassword?: string; data?: Record<string, any>; email?: string }) {
    this.init();
    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (this.session?.access_token) {
        headers["Authorization"] = `Bearer ${this.session.access_token}`;
      }
      if (this.authUser?.id) {
        headers["x-user-id"] = this.authUser.id;
      }
      if (this.authUser?.role) {
        headers["x-user-role"] = this.authUser.role;
      }

      const payload = {
        password: attributes.password,
        current_password: attributes.current_password || attributes.currentPassword,
        data: attributes.data,
      };

      const res = await fetch(`${API_BASE}/api/auth/update-user`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const parsed = await safeParseApiResponse<any>(res);
      if (!parsed.ok || parsed.error) {
        const errorMsg = parsed.error?.message || "Failed to update user profile";
        return { data: { user: null }, error: new Error(errorMsg) };
      }

      if (parsed.data?.user) {
        this.authUser = parsed.data.user;
        localStorage.setItem("accoupro_auth_user", JSON.stringify(this.authUser));
      }
      if (parsed.data?.session) {
        this.session = parsed.data.session;
        localStorage.setItem("accoupro_auth_session", JSON.stringify(this.session));
      }

      this.trigger("USER_UPDATED", this.session);
      return { data: { user: this.authUser }, error: null };
    } catch (err: any) {
      console.error("[MySQL Auth updateUser Error]:", err);
      return { data: { user: null }, error: err };
    }
  }

  async signOut(options?: { scope?: string }) {
    this.init();
    try {
      await fetch(`${API_BASE}/api/auth/logout`, { method: "POST" });
    } catch {
      // Ignore network errors on logout
    }

    this.authUser = null;
    this.session = null;
    localStorage.removeItem("accoupro_auth_user");
    localStorage.removeItem("accoupro_auth_session");

    this.trigger("SIGNED_OUT", null);
    return { error: null };
  }

  onAuthStateChange(callback: AuthListener) {
    this.init();
    this.listeners.push(callback);
    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this.listeners = this.listeners.filter((l) => l !== callback);
          },
        },
      },
    };
  }

  private trigger(event: string, session: Session | null) {
    for (const listener of this.listeners) {
      try {
        listener(event, session);
      } catch (err) {
        console.error("[MySQL Auth Listener Error]", err);
      }
    }
  }
}

interface FilterCondition {
  column: string;
  op: "eq" | "neq" | "gt" | "gte" | "lt" | "lte" | "like" | "in";
  value: unknown;
}

class QueryBuilder<T = any> implements PromiseLike<{ data: T | null; count: number | null; error: any }> {
  private tableName: string;
  private action: "select" | "insert" | "update" | "delete" = "select";
  private selectColumns: string[] | string = "*";
  private filters: FilterCondition[] = [];
  private orderClause: { column: string; ascending?: boolean } | null = null;
  private limitCount: number | null = null;
  private mutateData: unknown = null;
  private returnSingle: boolean = false;
  private returnMaybeSingle: boolean = false;

  constructor(tableName: string) {
    this.tableName = tableName;
  }

  select(columns: string[] | string = "*", options?: { count?: string; head?: boolean }) {
    // If called after insert/update, it indicates returning data
    if (this.action === "select") {
      this.selectColumns = columns;
    }
    return this;
  }

  insert(data: unknown) {
    this.action = "insert";
    this.mutateData = data;
    return this;
  }

  update(data: unknown) {
    this.action = "update";
    this.mutateData = data;
    return this;
  }

  delete() {
    this.action = "delete";
    return this;
  }

  eq(column: string, value: unknown) {
    this.filters.push({ column, op: "eq", value });
    return this;
  }

  neq(column: string, value: unknown) {
    this.filters.push({ column, op: "neq", value });
    return this;
  }

  gt(column: string, value: unknown) {
    this.filters.push({ column, op: "gt", value });
    return this;
  }

  gte(column: string, value: unknown) {
    this.filters.push({ column, op: "gte", value });
    return this;
  }

  lt(column: string, value: unknown) {
    this.filters.push({ column, op: "lt", value });
    return this;
  }

  lte(column: string, value: unknown) {
    this.filters.push({ column, op: "lte", value });
    return this;
  }

  like(column: string, value: unknown) {
    this.filters.push({ column, op: "like", value });
    return this;
  }

  ilike(column: string, value: unknown) {
    this.filters.push({ column, op: "like", value });
    return this;
  }

  in(column: string, value: unknown[]) {
    this.filters.push({ column, op: "in", value });
    return this;
  }

  order(column: string, { ascending = true }: { ascending?: boolean } = {}) {
    this.orderClause = { column, ascending };
    return this;
  }

  limit(n: number) {
    this.limitCount = n;
    return this;
  }

  single(): Promise<{ data: any; error: any }> {
    this.returnSingle = true;
    return this.execute().then((res) => {
      if (res.error) return res;
      if (Array.isArray(res.data)) {
        if (res.data.length === 0) {
          return { data: null, count: 0, error: { message: "JSON object requested, multiple (or no) rows returned" } };
        }
        return { data: res.data[0], count: 1, error: null };
      }
      return res;
    });
  }

  maybeSingle(): Promise<{ data: any; count: number | null; error: any }> {
    this.returnMaybeSingle = true;
    return this.execute().then((res) => {
      if (res.error) return res;
      if (Array.isArray(res.data)) {
        return { data: res.data[0] || null, count: res.data.length > 0 ? 1 : 0, error: null };
      }
      return res;
    });
  }

  private async execute(): Promise<{ data: any; count: number | null; error: any }> {
    try {
      const payload = {
        table: this.tableName,
        action: this.action,
        columns: this.selectColumns,
        filters: this.filters,
        data: this.mutateData,
        order: this.orderClause,
        limit: this.limitCount,
      };

      const headers = getAuthHeaders();

      const res = await fetch(`${API_BASE}/api/db/query`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const parsed = await safeParseApiResponse<any>(res);
      if (!parsed.ok || parsed.error) {
        return { data: null, count: null, error: { message: parsed.error?.message || "Database query failed" } };
      }

      const count = Array.isArray(parsed.data) ? parsed.data.length : parsed.data ? 1 : 0;
      return { data: parsed.data, count, error: null };
    } catch (err: any) {
      console.error(`[MySQL Query Error on ${this.tableName}]`, err);
      return { data: null, count: null, error: { message: err.message || "Network error connecting to MySQL backend" } };
    }
  }

  then<TResult1 = { data: T | null; count: number | null; error: any }, TResult2 = never>(
    onfulfilled?: ((value: { data: T | null; count: number | null; error: any }) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null
  ): Promise<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const savedSession = localStorage.getItem("accoupro_auth_session");
    const savedUser = localStorage.getItem("accoupro_auth_user");
    if (savedSession) {
      const parsed = JSON.parse(savedSession);
      if (parsed?.access_token) {
        headers["Authorization"] = `Bearer ${parsed.access_token}`;
      }
    }
    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      if (parsed?.id) headers["x-user-id"] = parsed.id;
      if (parsed?.role) headers["x-user-role"] = parsed.role;
    }
    const activeCompanyId = localStorage.getItem("accoupro_active_company_id");
    if (activeCompanyId) {
      headers["x-company-id"] = activeCompanyId;
    }
  } catch {}
  return headers;
}

async function fileToDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

class StorageFileApi {
  private bucket: string;

  constructor(bucket: string) {
    this.bucket = bucket;
  }

  async upload(path: string, file: File | Blob, options?: { upsert?: boolean }): Promise<{ data: any; error: any }> {
    try {
      const dataUrl = await fileToDataUrl(file);
      const fileName = (file as File).name || path.split("/").pop() || "document";
      const contentType = file.type || "application/octet-stream";

      const res = await fetch(`${API_BASE}/api/storage/upload`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          bucket: this.bucket,
          path,
          fileName,
          contentType,
          fileData: dataUrl,
        }),
      });

      const parsed = await safeParseApiResponse<any>(res);
      if (!parsed.ok || parsed.error) {
        return { data: null, error: { message: parsed.error?.message || "Upload failed" } };
      }

      return { data: parsed.data, error: null };
    } catch (err: any) {
      return { data: null, error: { message: err.message || "Storage upload error" } };
    }
  }

  async createSignedUrl(path: string, expiresIn: number = 86400 * 365): Promise<{ data: { signedUrl: string } | null; error: any }> {
    try {
      const res = await fetch(`${API_BASE}/api/storage/signed-url`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          bucket: this.bucket,
          path,
          expiresIn,
        }),
      });

      const parsed = await safeParseApiResponse<any>(res);
      if (!parsed.ok || parsed.error) {
        return { data: null, error: { message: parsed.error?.message || "Failed to create signed URL" } };
      }

      return { data: parsed.data, error: null };
    } catch (err: any) {
      return { data: null, error: { message: err.message || "Signed URL error" } };
    }
  }

  getPublicUrl(path: string): { data: { publicUrl: string } } {
    return {
      data: {
        publicUrl: `${API_BASE}/api/storage/files/${this.bucket}/${path}`,
      },
    };
  }
}

class StorageClient {
  from(bucket: string): StorageFileApi {
    return new StorageFileApi(bucket);
  }
}

class FunctionsClient {
  async invoke(functionName: string, options: { body?: any } = {}) {
    try {
      if (functionName === "ai-search") {
        const query = options.body?.query || "";
        let companyId = options.body?.companyId;
        try {
          if (!companyId) {
            companyId = localStorage.getItem("accoupro_active_company_id");
          }
        } catch {}

        const res = await fetch(`${API_BASE}/api/ai/search`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders()
          },
          body: JSON.stringify({ query, companyId }),
        });
        const parsed = await safeParseApiResponse<any>(res);
        if (!parsed.ok || parsed.error) return { data: null, error: parsed.error };
        return { data: parsed.data?.data || parsed.data, error: null };
      }
      if (functionName === "generate-report") {
        const res = await fetch(`${API_BASE}/api/ai/report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(options.body || {}),
        });
        const parsed = await safeParseApiResponse<any>(res);
        if (!parsed.ok || parsed.error) return { data: null, error: parsed.error };
        return { data: parsed.data, error: null };
      }
      if (functionName === "extract-invoice" || functionName === "auto-bookkeeping") {
        const text = options.body?.fileContent || options.body?.fileName || "";
        const res = await fetch(`${API_BASE}/api/ai/auto-entry`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, amount: options.body?.amount || 0 }),
        });
        const parsed = await safeParseApiResponse<any>(res);
        if (!parsed.ok || parsed.error) return { data: null, error: parsed.error };
        return { data: { data: parsed.data }, error: null };
      }
      return { data: null, error: { message: `Function ${functionName} not supported` } };
    } catch (err: any) {
      return { data: null, error: { message: err.message || "Function invocation failed" } };
    }
  }
}

class MySQLClient {
  auth: MySQLAuthClient;
  functions: FunctionsClient;
  storage: StorageClient;

  constructor() {
    this.auth = new MySQLAuthClient();
    this.functions = new FunctionsClient();
    this.storage = new StorageClient();
  }

  from<T = any>(tableName: string): QueryBuilder<T> {
    return new QueryBuilder<T>(tableName);
  }

  channel(name: string) {
    const ch = {
      on: (_event: string, _filter: any, _callback?: any) => ch,
      subscribe: (callback?: (status: string) => void) => {
        if (callback) callback("SUBSCRIBED");
        return ch;
      },
      unsubscribe: () => ({}),
    };
    return ch;
  }

  removeChannel(_channel: any) {
    return;
  }
}

export const supabase = new MySQLClient();
export default supabase;